/**
 * Generates a standalone, self-contained HTML document that hosts a clean, modern
 * multi-device preview suite (Desktop 1440px, Tablet 768px, Mobile 390px, and Full Window)
 * with accurate viewport dimensions, orientation toggling, zoom scaling, and neutral framing.
 */
export function createPreviewHubHtml(
  siteHtml: string,
  title: string = "Profolio Preview",
  subdomain?: string
): string {
  const safeTitle = title.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const siteUrl = subdomain ? `https://profolio.lk/@${subdomain}` : "https://profolio.lk/preview";
  // Encode site HTML safely in base64 to avoid escaping conflicts
  const encodedHtml = typeof window !== "undefined" && window.btoa
    ? window.btoa(unescape(encodeURIComponent(siteHtml)))
    : Buffer.from(siteHtml, "utf-8").toString("base64");

  return `<!DOCTYPE html>
<html lang="en" class="dark">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeTitle} — Viewport Preview</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    :root {
      --bg: #090d16;
      --card-bg: #0f172a;
      --border: #1e293b;
      --border-accent: #334155;
      --primary: #3b82f6;
      --primary-hover: #2563eb;
      --text: #f8fafc;
      --text-muted: #94a3b8;
    }
    body {
      font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
      background-color: var(--bg);
      color: var(--text);
      height: 100vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    header {
      height: 48px;
      background: rgba(15, 23, 42, 0.95);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-bottom: 1px solid var(--border);
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 16px;
      z-index: 50;
      flex-shrink: 0;
      user-select: none;
    }
    .brand-group {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .brand-title {
      font-size: 13px;
      font-weight: 700;
      color: #fff;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .brand-title span.badge {
      background: rgba(59, 130, 246, 0.15);
      color: #60a5fa;
      border: 1px solid rgba(59, 130, 246, 0.3);
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 9999px;
      font-weight: 600;
    }
    .dim-tag {
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px;
      color: var(--text-muted);
      background: #1e293b;
      padding: 2px 8px;
      border-radius: 6px;
    }
    .controls-center {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .btn-group {
      display: flex;
      background: #1e293b;
      padding: 2px;
      border-radius: 8px;
      border: 1px solid #334155;
    }
    .btn-group button {
      background: transparent;
      border: none;
      color: var(--text-muted);
      font-size: 11px;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 5px;
      transition: all 0.15s ease;
    }
    .btn-group button:hover {
      color: #fff;
    }
    .btn-group button.active {
      background: #fff;
      color: #0f172a;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
    .btn-action {
      background: #1e293b;
      border: 1px solid #334155;
      color: var(--text);
      font-size: 11px;
      font-weight: 600;
      padding: 5px 10px;
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      text-decoration: none;
      transition: all 0.15s ease;
    }
    .btn-action:hover {
      background: #334155;
      color: #fff;
    }
    .canvas-viewport {
      flex: 1;
      overflow: auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 24px;
      position: relative;
      min-height: 0;
    }
    /* Outer Scaler Wrapper - sets exact layout box in DOM */
    .scaler-wrapper {
      position: relative;
      flex-shrink: 0;
      margin: auto;
      transition: width 0.2s ease, height 0.2s ease;
    }
    /* Device Frames - neutral, minimal, modern */
    .device-container {
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      flex-direction: column;
      background: #0f172a;
      border: 1px solid rgba(255, 255, 255, 0.12);
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
      overflow: hidden;
      transform-origin: top left;
      transition: transform 0.2s ease;
    }
    .device-desktop {
      border-radius: 12px;
    }
    .device-tablet {
      border-radius: 16px;
    }
    .device-mobile {
      border-radius: 24px;
    }
    /* Raw Full Mode */
    .device-raw {
      position: static !important;
      width: 100% !important;
      height: 100% !important;
      border: none !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      transform: none !important;
    }
    /* Minimal Neutral Chrome Bar */
    .device-chrome {
      height: 32px;
      background: #1e293b;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 12px;
      gap: 12px;
      user-select: none;
      flex-shrink: 0;
    }
    .chrome-device-tag {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      font-weight: 500;
      color: #94a3b8;
    }
    .chrome-url-bar {
      flex: 1;
      max-width: 440px;
      background: #090d16;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 6px;
      height: 20px;
      display: flex;
      align-items: center;
      padding: 0 8px;
      gap: 6px;
      font-size: 10px;
      font-family: 'JetBrains Mono', monospace;
      color: #64748b;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .chrome-url-bar span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .chrome-size-tag {
      font-family: 'JetBrains Mono', monospace;
      font-size: 10px;
      color: #64748b;
    }
    .iframe-wrapper {
      flex: 1;
      width: 100%;
      height: 100%;
      min-height: 0;
      position: relative;
      background: #fff;
      overflow: hidden;
    }
    iframe {
      width: 100%;
      height: 100%;
      border: none;
      background: #fff;
      display: block;
    }
  </style>
</head>
<body>
  <header>
    <div class="brand-group">
      <div class="brand-title">
        ${safeTitle}
        <span class="badge">Preview</span>
      </div>
      <div id="dimTag" class="dim-tag">1440 × 900</div>
    </div>

    <div class="controls-center">
      <div class="btn-group" id="deviceGroup">
        <button id="btnDesktop" class="active" onclick="switchDevice('desktop')">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
          Desktop
        </button>
        <button id="btnTablet" onclick="switchDevice('tablet')">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="16" height="20" x="4" y="2" rx="2"/><line x1="12" x2="12.01" y1="18" y2="18"/></svg>
          Tablet
        </button>
        <button id="btnMobile" onclick="switchDevice('mobile')">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="14" height="20" x="5" y="2" rx="2"/><path d="M12 18h.01"/></svg>
          Mobile
        </button>
        <button id="btnRaw" onclick="switchDevice('raw')">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" x2="14" y1="3" y2="10"/><line x1="3" x2="10" y1="21" y2="14"/></svg>
          Full
        </button>
      </div>

      <div class="btn-group" id="orientGroup" style="display: none;">
        <button id="btnRotate" onclick="toggleOrientation()" title="Rotate Viewport (Portrait/Landscape)">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 21h5v-5"/></svg>
          <span id="orientLabel">Portrait</span>
        </button>
      </div>

      <div class="btn-group" id="zoomGroup">
        <button id="btnZoomFit" class="active" onclick="setZoom('fit')">Fit</button>
        <button id="btnZoom100" onclick="setZoom('100%')">100%</button>
        <button id="btnZoom75" onclick="setZoom('75%')">75%</button>
      </div>
    </div>

    <div style="display: flex; gap: 8px;">
      <button class="btn-action" onclick="openRawInNewWindow()" title="Open direct site in new window">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg>
        Raw Site
      </button>
    </div>
  </header>

  <main class="canvas-viewport" id="canvasViewport">
    <div id="scalerWrapper" class="scaler-wrapper">
      <div id="deviceWrapper" class="device-container device-desktop">
        <div id="deviceChrome" class="device-chrome">
          <div class="chrome-device-tag" id="chromeDeviceTag">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
            <span id="chromeDeviceName">Desktop</span>
          </div>
          <div class="chrome-url-bar">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <span>${siteUrl}</span>
          </div>
          <div class="chrome-size-tag" id="chromeSizeTag">1440 × 900</div>
        </div>
        <div class="iframe-wrapper">
          <iframe id="previewIframe" title="Portfolio Preview" sandbox="allow-scripts allow-same-origin"></iframe>
        </div>
      </div>
    </div>
  </main>

  <script>
    // Injected Site HTML payload
    var rawHtmlBase64 = "${encodedHtml}";
    var decodedHtml = decodeURIComponent(escape(atob(rawHtmlBase64)));
    var iframe = document.getElementById("previewIframe");
    iframe.srcdoc = decodedHtml;

    var currentDevice = "desktop";
    var isLandscape = false;
    var currentZoom = "fit";

    var scalerWrapper = document.getElementById("scalerWrapper");
    var deviceWrapper = document.getElementById("deviceWrapper");
    var canvas = document.getElementById("canvasViewport");
    var dimTag = document.getElementById("dimTag");
    var orientGroup = document.getElementById("orientGroup");
    var orientLabel = document.getElementById("orientLabel");
    var zoomGroup = document.getElementById("zoomGroup");
    var deviceChrome = document.getElementById("deviceChrome");
    var chromeDeviceName = document.getElementById("chromeDeviceName");
    var chromeSizeTag = document.getElementById("chromeSizeTag");

    var CHROME_HEIGHT = 32;

    function applyDimensions() {
      // Toggle orientation visibility
      orientGroup.style.display = (currentDevice === "tablet" || currentDevice === "mobile") ? "flex" : "none";
      zoomGroup.style.display = currentDevice === "raw" ? "none" : "flex";

      if (currentDevice === "raw") {
        scalerWrapper.style.width = "100%";
        scalerWrapper.style.height = "100%";
        scalerWrapper.style.margin = "0";
        deviceWrapper.className = "device-container device-raw";
        deviceWrapper.style.width = "100%";
        deviceWrapper.style.height = "100%";
        deviceWrapper.style.transform = "none";
        deviceChrome.style.display = "none";
        dimTag.textContent = "Full Window";
        return;
      }

      deviceChrome.style.display = "flex";
      deviceWrapper.className = "device-container";

      var w, viewportH;
      if (currentDevice === "desktop") {
        deviceWrapper.classList.add("device-desktop");
        w = 1440;
        viewportH = 900;
        chromeDeviceName.textContent = "Desktop";
        chromeSizeTag.textContent = "1440 × 900";
        dimTag.textContent = "1440 × 900 (Desktop)";
      } else if (currentDevice === "tablet") {
        deviceWrapper.classList.add("device-tablet");
        if (isLandscape) {
          w = 1024;
          viewportH = 768;
          chromeDeviceName.textContent = "Tablet Landscape";
          chromeSizeTag.textContent = "1024 × 768";
          dimTag.textContent = "1024 × 768 (Tablet)";
        } else {
          w = 768;
          viewportH = 1024;
          chromeDeviceName.textContent = "Tablet";
          chromeSizeTag.textContent = "768 × 1024";
          dimTag.textContent = "768 × 1024 (Tablet)";
        }
      } else if (currentDevice === "mobile") {
        deviceWrapper.classList.add("device-mobile");
        if (isLandscape) {
          w = 844;
          viewportH = 390;
          chromeDeviceName.textContent = "Mobile Landscape";
          chromeSizeTag.textContent = "844 × 390";
          dimTag.textContent = "844 × 390 (Mobile)";
        } else {
          w = 390;
          viewportH = 844;
          chromeDeviceName.textContent = "Mobile";
          chromeSizeTag.textContent = "390 × 844";
          dimTag.textContent = "390 × 844 (Mobile)";
        }
      }

      var totalH = viewportH + CHROME_HEIGHT;

      // Calculate scale factor
      var scale = 1;
      if (currentZoom === "75%") {
        scale = 0.75;
      } else if (currentZoom === "100%") {
        scale = 1;
      } else {
        // "fit" mode: ensure both width and height fit inside canvas with 48px padding
        var availableW = Math.max(canvas.clientWidth - 48, 200);
        var availableH = Math.max(canvas.clientHeight - 48, 200);
        scale = Math.min(1, availableW / w, availableH / totalH);
      }

      // Outer scaler layout box in parent flow
      scalerWrapper.style.width = Math.round(w * scale) + "px";
      scalerWrapper.style.height = Math.round(totalH * scale) + "px";
      scalerWrapper.style.margin = "auto";

      // Inner device container scaled from top left
      deviceWrapper.style.width = w + "px";
      deviceWrapper.style.height = totalH + "px";
      deviceWrapper.style.transform = "scale(" + scale + ")";
    }

    function switchDevice(dev) {
      currentDevice = dev;
      document.querySelectorAll("#deviceGroup button").forEach(function(b) { b.classList.remove("active"); });
      if (dev === "desktop") document.getElementById("btnDesktop").classList.add("active");
      if (dev === "tablet") document.getElementById("btnTablet").classList.add("active");
      if (dev === "mobile") document.getElementById("btnMobile").classList.add("active");
      if (dev === "raw") document.getElementById("btnRaw").classList.add("active");
      applyDimensions();
    }

    function toggleOrientation() {
      isLandscape = !isLandscape;
      orientLabel.textContent = isLandscape ? "Landscape" : "Portrait";
      applyDimensions();
    }

    function setZoom(zoom) {
      currentZoom = zoom;
      document.querySelectorAll("#zoomGroup button").forEach(function(b) { b.classList.remove("active"); });
      if (zoom === "fit") document.getElementById("btnZoomFit").classList.add("active");
      if (zoom === "100%") document.getElementById("btnZoom100").classList.add("active");
      if (zoom === "75%") document.getElementById("btnZoom75").classList.add("active");
      applyDimensions();
    }

    function openRawInNewWindow() {
      var blob = new Blob([decodedHtml], { type: "text/html" });
      var url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }

    window.addEventListener("resize", function() {
      if (currentZoom === "fit") applyDimensions();
    });

    // Keyboard Shortcuts (1=desktop, 2=tablet, 3=mobile, r=rotate)
    window.addEventListener("keydown", function(e) {
      if (e.key === "1") switchDevice("desktop");
      if (e.key === "2") switchDevice("tablet");
      if (e.key === "3") switchDevice("mobile");
      if (e.key === "r" || e.key === "R") {
        if (currentDevice === "tablet" || currentDevice === "mobile") toggleOrientation();
      }
    });

    applyDimensions();
  </script>
</body>
</html>`;
}
